import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';

// Public pages
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import AdminLoginPage from './pages/AdminLoginPage';
import OpeningsPage from './pages/OpeningsPage';
import OpeningDetailPage from './pages/OpeningDetailPage';
import ApplyChoicePage from './pages/ApplyChoicePage';
import AssessmentPortalStudy from './pages/AssessmentPortalStudy';
import AssessmentPortalJob from './pages/AssessmentPortalJob';
import AboutPage from './pages/AboutPage';
import WhyChooseCountryPage from './pages/WhyChooseCountryPage';
import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FAQPage';
import TestimonialsPage from './pages/TestimonialsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CourseDirectoryPage from './pages/CourseDirectoryPage';
import UniversityDirectoryPage from './pages/UniversityDirectoryPage';
import UniversityDetailPage from './pages/UniversityDetailPage';
import InstitutionCourseTagsPage from './pages/InstitutionCourseTagsPage';
import InstitutionDetailPage from './pages/InstitutionDetailPage';
import AdminCoursesPage from './pages/AdminCoursesPage';
import AgentAcceptInvitePage from './pages/AgentAcceptInvitePage';

// Portal pages (authenticated client/student)
import PortalDashboard from './pages/PortalDashboard';
import ApplicationTracker from './pages/ApplicationTracker';
import DocumentsUpload from './pages/DocumentUploadPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import NotificationsPage from './pages/NotificationsPage';

// Admin pages
import AdminOverViewPage from './pages/AdminOverviewPage';
import AdminApplicationsPage from './pages/AdminApplicationsPage';
import AdminApplicationDetailPage from './pages/AdminApplicationDetailPage';
import AdminAgentsPage from './pages/AdminAgentsPage';
import AdminOpeningsPage from './pages/AdminOpeningsPage';
import AdminInstitutionsPage from './pages/AdminInstitutionsPage';
import AdminTestimonialsPage from './pages/AdminTestimonialsPage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import AdminCourseImportPage from './pages/AdminCourseImportPage';
import AdminCourseReviewPage from './pages/AdminCourseReviewPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminProfilePage from './pages/AdminProfilePage';
import AdminFaqPage from './pages/AdminFaqPage';
import AdminNewsletterPage from './pages/AdminNewsletterPage';
import AdminCountryGuidesPage from './pages/AdminCountryGuidesPage';

// Agent pages
import AgentApplicationsPage from './pages/AgentApplicationsPage';
import AgentApplicationDetailPage from './pages/AgentApplicationDetailPage';
import AgentPaymentsPage from './pages/AgentPaymentsPage';
import AgentOpeningsPage from './pages/AgentOpeningsPage';
import AgentCourseImportPage from './pages/AgentCourseImportPage';
import AgentCourseReviewPage from './pages/AgentCourseReviewPage';
import AgentInstitutionsPage from './pages/AgentInstitutionsPage';
import AgentCountryGuidesPage from './pages/AgentCountryGuidesPage';

// Route guards
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AgentProtectedRoute from './components/AgentProtectedRoute';

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* ---------- Public site ---------- */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/openings" element={<OpeningsPage />} />
        <Route path="/openings/:id" element={<OpeningDetailPage />} />
        <Route path="/apply" element={<ApplyChoicePage />} />
        <Route path="/apply/study" element={<AssessmentPortalStudy />} />
        <Route path="/apply/job" element={<AssessmentPortalJob />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/why-choose/:slug" element={<WhyChooseCountryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/courses" element={<CourseDirectoryPage />} />
        <Route path="/universities" element={<UniversityDirectoryPage />} />
        <Route path="/universities/:id" element={<UniversityDetailPage />} />

        {/* Agent invite acceptance MUST be public — the agent has no
            token yet, that's the entire point of this page. */}
        <Route path="/agent/accept-invite" element={<AgentAcceptInvitePage />} />

        {/* ---------- Student/Client Portal (protected) ---------- */}
        <Route path="/portal/dashboard" element={<ProtectedRoute><PortalDashboard /></ProtectedRoute>} />
        <Route path="/portal/applications/:id" element={<ProtectedRoute><ApplicationTracker /></ProtectedRoute>} />
        <Route path="/portal/applications/:id/documents" element={<ProtectedRoute><DocumentsUpload /></ProtectedRoute>} />
        <Route path="/portal/settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
        <Route path="/portal/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

        {/* ---------- Admin Dashboard (protected) ---------- */}
        <Route path="/admin/overview" element={<AdminProtectedRoute><AdminOverViewPage /></AdminProtectedRoute>} />
        <Route path="/admin/applications" element={<AdminProtectedRoute><AdminApplicationsPage /></AdminProtectedRoute>} />
        <Route path="/admin/applications/:id" element={<AdminProtectedRoute><AdminApplicationDetailPage /></AdminProtectedRoute>} />
        <Route path="/admin/agents" element={<AdminProtectedRoute><AdminAgentsPage /></AdminProtectedRoute>} />
        <Route path="/admin/openings" element={<AdminProtectedRoute><AdminOpeningsPage /></AdminProtectedRoute>} />
        <Route path="/admin/institutions" element={<AdminProtectedRoute><AdminInstitutionsPage /></AdminProtectedRoute>} />
        <Route path="/admin/institutions/:id" element={<AdminProtectedRoute><InstitutionDetailPage isAdmin /></AdminProtectedRoute>} />
        <Route path="/admin/institutions/:id/courses" element={<AdminProtectedRoute><InstitutionCourseTagsPage isAdmin /></AdminProtectedRoute>} />
        <Route path="/admin/courses" element={<AdminProtectedRoute><AdminCoursesPage /></AdminProtectedRoute>} />
        <Route path="/admin/testimonials" element={<AdminProtectedRoute><AdminTestimonialsPage /></AdminProtectedRoute>} />
        <Route path="/admin/payments" element={<AdminProtectedRoute><AdminPaymentsPage /></AdminProtectedRoute>} />
        <Route path="/admin/courses/import" element={<AdminProtectedRoute><AdminCourseImportPage /></AdminProtectedRoute>} />
        <Route path="/admin/courses/review" element={<AdminProtectedRoute><AdminCourseReviewPage /></AdminProtectedRoute>} />
        <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettingsPage /></AdminProtectedRoute>} />
        <Route path="/admin/profile" element={<AdminProtectedRoute><AdminProfilePage /></AdminProtectedRoute>} />
        <Route path="/admin/faqs" element={<AdminProtectedRoute><AdminFaqPage /></AdminProtectedRoute>} />
        <Route path="/admin/newsletter" element={<AdminProtectedRoute><AdminNewsletterPage /></AdminProtectedRoute>} />
        <Route path="/admin/country-guides" element={<AdminProtectedRoute><AdminCountryGuidesPage /></AdminProtectedRoute>} />

        {/* ---------- Agent Workspace (protected) ---------- */}
        <Route path="/agent/applications" element={<AgentProtectedRoute><AgentApplicationsPage /></AgentProtectedRoute>} />
        <Route path="/agent/applications/:id" element={<AgentProtectedRoute><AgentApplicationDetailPage /></AgentProtectedRoute>} />
        <Route path="/agent/payments" element={<AgentProtectedRoute><AgentPaymentsPage /></AgentProtectedRoute>} />
        <Route path="/agent/openings" element={<AgentProtectedRoute><AgentOpeningsPage /></AgentProtectedRoute>} />
        <Route path="/agent/courses/import" element={<AgentProtectedRoute><AgentCourseImportPage /></AgentProtectedRoute>} />
        <Route path="/agent/courses/review" element={<AgentProtectedRoute><AgentCourseReviewPage /></AgentProtectedRoute>} />
        <Route path="/agent/institutions" element={<AgentProtectedRoute><AgentInstitutionsPage /></AgentProtectedRoute>} />
        <Route path="/agent/institutions/:id" element={<AgentProtectedRoute><InstitutionDetailPage /></AgentProtectedRoute>} />
        <Route path="/agent/institutions/:id/courses" element={<AgentProtectedRoute><InstitutionCourseTagsPage /></AgentProtectedRoute>} />
        <Route path="/agent/country-guides" element={<AgentProtectedRoute><AgentCountryGuidesPage /></AgentProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}